import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getBuyerParentOrders } from '../utils/multiVendorOrderApi';
import { CancellationBreakdown, CancellationSummary } from './CancellationBreakdown';
import { 
  ArrowUp, 
  Star, 
  Calendar,
  ArrowDown,
  TickSquare
} from 'react-iconly';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};

const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

/**
 * Buyer Cancellation Dashboard
 * Shows customer's cancelled orders and refund status
 */
export function BuyerCancellationDashboard() {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);

  useEffect(() => {
    if (!currentUser) return;

    const loadCancelledOrders = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Get all parent orders for the buyer
        const parentOrders = await getBuyerParentOrders(currentUser.uid);
        
        // Filter for cancelled orders
        const cancelledOrders = parentOrders.filter(order => 
          order.status === 'canceled' || order.status === 'cancelled'
        );

        // Sort by cancellation date (most recent first)
        cancelledOrders.sort((a, b) => {
          const dateA = a.cancelledAt?.toDate ? a.cancelledAt.toDate() : new Date(a.cancelledAt);
          const dateB = b.cancelledAt?.toDate ? b.cancelledAt.toDate() : new Date(b.cancelledAt);
          return dateB - dateA;
        });

        setOrders(cancelledOrders);
      } catch (err) {
        console.error('Error loading cancelled orders:', err);
        setError('Failed to load cancellation data');
      } finally {
        setLoading(false);
      }
    };

    loadCancelledOrders();
  }, [currentUser]);

  // Calculate refund metrics
  const metrics = React.useMemo(() => {
    const totalCancellations = orders.length;
    const totalRefunded = orders.reduce((sum, order) => {
      return sum + (order.breakdown?.grossOrderAmount || 0);
    }, 0);
    const averageRefund = totalCancellations > 0 ? totalRefunded / totalCancellations : 0;
    const pendingRefunds = orders.filter(order => 
      order.refundStatus === 'pending'
    ).length;

    return {
      totalCancellations,
      totalRefunded,
      averageRefund,
      pendingRefunds
    };
  }, [orders]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatOrderNumber = (orderId) => {
    if (!orderId) return 'Order #000000';
    const shortId = orderId.slice(-6).toUpperCase();
    return `Order #${shortId}`;
  };

  const getRefundStatusColor = (status) => {
    switch (status) {
      case 'processed': return SUCCESS_GREEN;
      case 'pending': return WARNING_ORANGE;
      case 'failed': return ERROR_RED;
      default: return ACCENT_GRAY;
    }
  };

  const getRefundStatusLabel = (status) => {
    switch (status) {
      case 'processed': return 'Refunded';
      case 'pending': return 'Processing';
      case 'failed': return 'Failed';
      default: return 'Unknown';
    }
  };

  const handleViewBreakdown = (order) => {
    setSelectedOrder(order);
    setShowBreakdown(true);
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: NEUTRAL_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', color: ACCENT_GRAY }}>
          <div style={{ fontSize: 18, marginBottom: 8 }}>Loading cancellations...</div>
          <div style={{ fontSize: 14 }}>Please wait</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: NEUTRAL_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', color: ERROR_RED }}>
          <div style={{ fontSize: 18, marginBottom: 8 }}>Error</div>
          <div style={{ fontSize: 14 }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: NEUTRAL_BG }}>
      {/* Header */}
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: NEUTRAL_WHITE,
        borderBottom: '1px solid #eee',
        padding: '12px 0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 20px' }}>
          <h1 style={{
            ...headingFont,
            fontSize: 22,
            color: NEUTRAL_TEXT,
            margin: 0
          }}>
            My Cancellations
          </h1>
        </div>
      </header>

      <div style={{ padding: '20px', maxWidth: 1000, margin: '0 auto' }}>
        {/* Metrics Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
          marginBottom: 24
        }}>
          {/* Total Refunded */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: 20,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 8
            }}>
              <Star set="regular" size={24} primaryColor={SUCCESS_GREEN} />
              <span style={{
                ...headingFont,
                fontSize: 24,
                color: SUCCESS_GREEN
              }}>
                ${metrics.totalRefunded.toFixed(2)}
              </span>
            </div>
            <div style={{
              fontSize: 14,
              color: ACCENT_GRAY,
              ...bodyFont
            }}>
              Total Refunded
            </div>
          </div>

          {/* Total Cancellations */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: 20,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 8
            }}>
              <ArrowUp set="regular" size={24} primaryColor={PRIMARY_BROWN} />
              <span style={{
                ...headingFont,
                fontSize: 24,
                color: PRIMARY_BROWN
              }}>
                {metrics.totalCancellations}
              </span>
            </div>
            <div style={{
              fontSize: 14,
              color: ACCENT_GRAY,
              ...bodyFont
            }}>
              Orders Cancelled
            </div>
          </div>

          {/* Average Refund */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: 20,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 8
            }}>
              <ArrowUp set="regular" size={24} primaryColor={SUCCESS_GREEN} />
              <span style={{
                ...headingFont,
                fontSize: 24,
                color: SUCCESS_GREEN
              }}>
                ${metrics.averageRefund.toFixed(2)}
              </span>
            </div>
            <div style={{
              fontSize: 14,
              color: ACCENT_GRAY,
              ...bodyFont
            }}>
              Avg Refund
            </div>
          </div>

          {/* Pending Refunds */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: 20,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 8
            }}>
              <TickSquare set="regular" size={24} primaryColor={WARNING_ORANGE} />
              <span style={{
                ...headingFont,
                fontSize: 24,
                color: WARNING_ORANGE
              }}>
                {metrics.pendingRefunds}
              </span>
            </div>
            <div style={{
              fontSize: 14,
              color: ACCENT_GRAY,
              ...bodyFont
            }}>
              Processing
            </div>
          </div>
        </div>

        {/* Cancelled Orders List */}
        <div style={{
          background: NEUTRAL_WHITE,
          borderRadius: 12,
          padding: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{
            ...headingFont,
            fontSize: 18,
            color: NEUTRAL_TEXT,
            margin: '0 0 16px 0'
          }}>
            Cancelled Orders
          </h2>

          {orders.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: ACCENT_GRAY
            }}>
              <TickSquare set="regular" size={48} primaryColor={ACCENT_GRAY} />
              <div style={{ fontSize: 16, marginTop: 12, ...bodyFont }}>
                No cancelled orders found
              </div>
              <div style={{ fontSize: 14, marginTop: 4, ...bodyFont }}>
                Your cancelled orders will appear here
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {orders.map((order) => (
                <div
                  key={order.id}
                  style={{
                    border: '1px solid #E0E0E0',
                    borderRadius: 8,
                    padding: 16,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => handleViewBreakdown(order)}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8
                  }}>
                    <div>
                      <h3 style={{
                        ...headingFont,
                        fontSize: 16,
                        color: NEUTRAL_TEXT,
                        margin: '0 0 4px 0'
                      }}>
                        {formatOrderNumber(order.id)}
                      </h3>
                      <div style={{
                        fontSize: 14,
                        color: ACCENT_GRAY,
                        ...bodyFont
                      }}>
                        Cancelled: {formatDate(order.cancelledAt)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: 16,
                        color: NEUTRAL_TEXT,
                        fontWeight: 600,
                        ...bodyFont
                      }}>
                        ${(order.breakdown?.grossOrderAmount || 0).toFixed(2)}
                      </div>
                      <div style={{
                        fontSize: 12,
                        color: getRefundStatusColor(order.refundStatus || 'processed'),
                        fontWeight: 600,
                        ...bodyFont
                      }}>
                        {getRefundStatusLabel(order.refundStatus || 'processed')}
                      </div>
                      <CancellationSummary
                        refundBreakdown={order.breakdown}
                        orderState={order.orderStates?.current || 'canceled'}
                        initiatedBy={order.cancelledBy}
                        viewType="buyer"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Breakdown Modal */}
      {showBreakdown && selectedOrder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: 24,
            maxWidth: 600,
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowBreakdown(false)}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: 'none',
                border: 'none',
                fontSize: 24,
                color: ACCENT_GRAY,
                cursor: 'pointer'
              }}
            >
              ×
            </button>
            
            <CancellationBreakdown
              refundBreakdown={selectedOrder.breakdown}
              orderState={selectedOrder.orderStates?.current || 'canceled'}
              initiatedBy={selectedOrder.cancelledBy}
              cancelledAt={selectedOrder.cancelledAt}
              cancellationReason={selectedOrder.cancellationReason}
              viewType="buyer"
            />
          </div>
        </div>
      )}
    </div>
  );
}
